<template>
  <v-app class="scan-qr-layout">
    <v-app-bar app flat height="65" class="qr-bar" :class="{ 'qr-bar--scrolled': scrolled }">
      <div class="container-xl d-flex align-center px-0" style="gap: 10px">
        <ScanQrBarLogo />
        <v-toolbar-title class="flex-grow-1 min-width-0">{{ $t("cart") }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <button
          v-if="defineStore.cart.length"
          class="qr-clear-btn mr-2"
          @click="clearAllItems"
        >{{ $t("remove_all") || "Remove all" }}</button>
        <QrLanguageMenu />
      </div>
    </v-app-bar>

    <v-main class="scan-cart-page">
      <div class="container-xl qr-cart-content">

        <!-- Empty state -->
        <div v-if="!defineStore.cart.length" class="qr-cart-empty">
          <div class="qr-cart-empty__inner">
            <v-img
              src="/images/no_food_photo.svg"
              contain
              max-width="140"
              class="mx-auto"
              alt=""
            />
            <h3 class="pt-6 text-h6 qr-text">{{ $t("cart_empty") }}</h3>
            <p class="qr-muted-text mb-0">{{ $t("cart_empty_description") }}</p>
          </div>
        </div>

        <template v-if="defineStore.cart.length">

          <!-- Buffet plan card -->
          <div v-if="isBuffetScan && buffetLines.length" class="qr-cart-plan-card mb-4">
            <div class="qr-cart-plan-card__header">
              <div class="qr-cart-plan-card__icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M7 3v8a3 3 0 003 3v7M7 3v6M10 3v6M17 3c-1.5 0-3 1.5-3 4s1.5 4 3 4v10"
                    stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                </svg>
              </div>
              <div class="qr-cart-plan-card__info">
                <div class="qr-cart-plan-card__name">{{ $t("buffet_plans") }}</div>
                <div class="qr-cart-plan-card__sub">{{ $t("buffet_persons") }} · {{ $t("included") }}</div>
              </div>
              <div class="qr-cart-plan-card__badge">{{ $t("confirmed") || "Confirmed" }}</div>
            </div>
            <div class="qr-cart-plan-card__body">
              <div>
                <div class="qr-cart-plan-card__plan-name">
                  {{ buffetHeroPlans.map(p => p.plan_name || p.planName).join(', ') }}
                </div>
                <div class="qr-cart-plan-card__plan-detail" v-if="buffetPlanDetailCaption">
                  {{ buffetHeroGuests }} {{ $t("buffet_persons") }} · {{ buffetPlanDetailCaption }}
                </div>
              </div>
              <div class="qr-cart-plan-card__total">
                {{ cartCurrencySymbol }}{{ buffetPlanPortion | NumberFormat }}
              </div>
            </div>
          </div>

          <!-- Buffet items section -->
          <div v-if="isBuffetScan && buffetLines.length" class="qr-cart-section">
            <div class="qr-cart-section__header">
              <div class="qr-cart-section__title">{{ $t("buffet_plans") }}</div>
              <div class="qr-cart-section__sub">{{ buffetLines.length }} {{ $t("added") || "added" }} · {{ $t("no_extra_charge") || "no extra charge" }}</div>
            </div>
            <div class="qr-cart-lines">
              <div
                v-for="(item, index) in buffetLines"
                :key="'b-' + index"
                class="qr-cart-line-card"
              >
                <div class="qr-cart-line-card__thumb">
                  <ScanProductImage :img="item.item.img" :contain="true" width="56" height="56" />
                </div>
                <div class="qr-cart-line-card__info">
                  <div class="qr-cart-line-card__name">{{ getProductName(item.item) }}</div>
                  <div class="qr-cart-line-card__sub qr-included-text">
                    {{ $t("included_in_plan") }} × {{ item.qty }}
                  </div>
                </div>
                <div class="qr-qty-stepper">
                  <button
                    class="qr-qty-stepper__btn"
                    :aria-label="$t('remove_from_cart')"
                    @click="decrementCart(item)"
                  >−</button>
                  <span class="qr-qty-stepper__count">{{ item.qty }}</span>
                  <button
                    class="qr-qty-stepper__btn qr-qty-stepper__btn--plus"
                    :aria-label="$t('add')"
                    @click="addToCartLine(item)"
                  >+</button>
                </div>
                <div class="qr-cart-line-card__total qr-included-text">
                  {{ getCurrencySymbol(item.item) }}{{ 0 | NumberFormat }}
                </div>
              </div>
            </div>
          </div>

          <!-- Extras section (buffet) -->
          <div v-if="isBuffetScan && extraLines.length" class="qr-cart-section">
            <div class="qr-cart-section__header">
              <div class="qr-cart-section__title">{{ $t("paid_extra") }}</div>
              <div class="qr-cart-section__sub">{{ extraLines.length }} {{ $t("added") || "added" }} · {{ $t("charged_separately") || "charged at checkout" }}</div>
            </div>
            <div class="qr-cart-lines">
              <div
                v-for="(item, index) in extraLines"
                :key="'e-' + index"
                class="qr-cart-line-card"
              >
                <div class="qr-cart-line-card__thumb">
                  <ScanProductImage :img="item.item.img" :contain="true" width="56" height="56" />
                </div>
                <div class="qr-cart-line-card__info">
                  <div class="qr-cart-line-card__name">{{ getProductName(item.item) }}</div>
                  <div class="qr-cart-line-card__sub">
                    {{ item.qty }} × {{ getCurrencySymbol(item.item) }}{{ unitLinePrice(item) | NumberFormat }}
                  </div>
                </div>
                <div class="qr-qty-stepper">
                  <button
                    class="qr-qty-stepper__btn"
                    :aria-label="$t('remove_from_cart')"
                    @click="decrementCart(item)"
                  >−</button>
                  <span class="qr-qty-stepper__count">{{ item.qty }}</span>
                  <button
                    class="qr-qty-stepper__btn qr-qty-stepper__btn--plus"
                    :aria-label="$t('add')"
                    @click="addToCartLine(item)"
                  >+</button>
                </div>
                <div class="qr-cart-line-card__total qr-accent-text">
                  {{ getCurrencySymbol(item.item) }}{{ item.price | NumberFormat }}
                </div>
              </div>
            </div>
          </div>

          <!-- Simple items (non-buffet) -->
          <div v-if="!isBuffetScan" class="qr-cart-section">
            <div class="qr-cart-lines">
              <div
                v-for="(item, index) in defineStore.cart"
                :key="'n-' + index"
                class="qr-cart-line-card"
              >
                <div class="qr-cart-line-card__thumb">
                  <ScanProductImage :img="item.item.img" :contain="true" width="56" height="56" />
                </div>
                <div class="qr-cart-line-card__info">
                  <div class="qr-cart-line-card__name">{{ getProductName(item.item) }}</div>
                  <div class="qr-cart-line-card__sub">
                    {{ item.qty }} × {{ getCurrencySymbol(item.item) }}{{ unitLinePrice(item) | NumberFormat }}
                  </div>
                </div>
                <div class="qr-qty-stepper">
                  <button
                    class="qr-qty-stepper__btn"
                    :aria-label="$t('remove_from_cart')"
                    @click="decrementCart(item)"
                  >−</button>
                  <span class="qr-qty-stepper__count">{{ item.qty }}</span>
                  <button
                    class="qr-qty-stepper__btn qr-qty-stepper__btn--plus"
                    :aria-label="$t('add')"
                    @click="addToCartLine(item)"
                  >+</button>
                </div>
                <div class="qr-cart-line-card__total qr-accent-text">
                  {{ getCurrencySymbol(item.item) }}{{ item.price | NumberFormat }}
                </div>
              </div>
            </div>
          </div>

          <!-- Totals breakdown -->
          <div class="qr-cart-totals-card mb-3">
            <div v-if="isBuffetScan && buffetPlanPortion > 0" class="qr-totals-row">
              <span>{{ $t("buffet_plans") }} × {{ buffetHeroGuests }}</span>
              <span>{{ cartCurrencySymbol }}{{ buffetPlanPortion | NumberFormat }}</span>
            </div>
            <div v-if="isBuffetScan" class="qr-totals-row">
              <span>{{ $t("paid_extra") }}</span>
              <span>{{ cartCurrencySymbol }}{{ extrasSubtotal | NumberFormat }}</span>
            </div>
            <div v-if="!isBuffetScan" class="qr-totals-row">
              <span>{{ $t("amount_to_pay") }}</span>
              <span>{{ cartCurrencySymbol }}{{ simpleCartTotal | NumberFormat }}</span>
            </div>
            <div class="qr-totals-row">
              <span>{{ $t("service_charge") || "Service charge" }}</span>
              <span>{{ $t("included") }}</span>
            </div>
          </div>

          <!-- Notes button -->
          <button class="qr-notes-btn mb-4" disabled aria-label="Add kitchen note">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M4 7h16M4 12h16M4 17h10" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
            <span>{{ $t("add_kitchen_note") }}</span>
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
              <path d="M3 1l5 5-5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>

        </template>
      </div>
    </v-main>

    <!-- Sticky bottom bar -->
    <div
      v-if="defineStore.cart.length"
      class="qr-cart-sticky-bar"
      role="region"
      :aria-label="$t('amount_to_pay')"
    >
      <div class="qr-cart-sticky-bar__total">
        <div class="qr-cart-sticky-bar__label">{{ $t("amount_to_pay") }}</div>
        <div class="qr-cart-sticky-bar__price">
          {{ cartCurrencySymbol }}{{ (isBuffetScan ? grandScanTotal : simpleCartTotal) | NumberFormat }}
        </div>
      </div>
      <button
        class="qr-btn-cta qr-cart-sticky-bar__cta"
        :disabled="!defineStore.cart.length"
        :aria-label="$t('send_to_kitchen')"
        @click="openConfirmSheet"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" style="flex-shrink:0">
          <path d="M7 3v8a3 3 0 003 3v7M7 3v6M10 3v6M17 3c-1.5 0-3 1.5-3 4s1.5 4 3 4v10"
            stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        {{ $t("send_to_kitchen") }}
      </button>
    </div>

    <app-loading :overlay="overlay" />

    <!-- Confirmation bottom sheet -->
    <v-bottom-sheet
      v-model="confirmSheet"
      :persistent="submitting"
      scrollable
      content-class="rounded-t-xl qr-confirm-sheet"
    >
      <v-sheet class="rounded-t-xl pa-4 pb-8 qr-confirm-sheet-inner">
        <div class="qr-sheet-grabber" aria-hidden="true" />

        <!-- Confirm state -->
        <template v-if="!submitted">
          <div class="qr-sheet-eyebrow text-center">{{ $t("confirm_order") }}</div>
          <div class="qr-sheet-title text-center mt-1">{{ $t("send_to_kitchen") }}?</div>
          <p class="qr-sheet-desc text-center mt-2 mb-0">
            {{ $t("order_confirm_desc") || "Items will be cooked and brought to your table. You can keep adding more anytime." }}
          </p>

          <!-- Summary card -->
          <div class="qr-sheet-summary-card mt-4">
            <div class="qr-sheet-summary-row">
              <div class="qr-sheet-summary-row__icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <circle cx="9" cy="8" r="3.5" stroke="currentColor" stroke-width="1.7" fill="none"/>
                  <circle cx="17" cy="9" r="2.8" stroke="currentColor" stroke-width="1.7" fill="none"/>
                  <path d="M3 19c0-3 2.5-5 6-5s6 2 6 5M14.5 19c.5-2 2.5-3.5 5-3.5s4 1 4.5 3" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" fill="none"/>
                </svg>
              </div>
              <span class="qr-sheet-summary-row__label">{{ $t("buffet_persons") }}</span>
              <span class="qr-sheet-summary-row__value">{{ buffetHeroGuests || defineStore.cart.length }} {{ $t("items") }}</span>
            </div>
            <div class="qr-sheet-summary-row" v-if="isBuffetScan && buffetPlanPortion > 0">
              <div class="qr-sheet-summary-row__icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M5 3h14v18l-3-2-2 2-2-2-2 2-2-2-3 2V3z" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" fill="none"/>
                </svg>
              </div>
              <span class="qr-sheet-summary-row__label">{{ $t("buffet_plans") }}</span>
              <span class="qr-sheet-summary-row__value qr-mono">{{ cartCurrencySymbol }}{{ buffetPlanPortion | NumberFormat }}</span>
            </div>
            <div class="qr-sheet-summary-row" v-if="isBuffetScan && extrasSubtotal > 0">
              <div class="qr-sheet-summary-row__icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M6 8h12l-1.5 11a2 2 0 01-2 1.7H9.5a2 2 0 01-2-1.7L6 8z" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" fill="none"/>
                </svg>
              </div>
              <span class="qr-sheet-summary-row__label">{{ $t("paid_extra") }}</span>
              <span class="qr-sheet-summary-row__value qr-mono">{{ cartCurrencySymbol }}{{ extrasSubtotal | NumberFormat }}</span>
            </div>
          </div>

          <!-- Grand total band -->
          <div class="qr-sheet-grand-total mt-3">
            <span class="qr-sheet-grand-total__label">{{ $t("amount_to_pay") }}</span>
            <span class="qr-sheet-grand-total__value">
              {{ cartCurrencySymbol }}{{ (isBuffetScan ? grandScanTotal : simpleCartTotal) | NumberFormat }}
            </span>
          </div>

          <!-- Actions -->
          <div class="qr-sheet-actions mt-4">
            <button
              class="qr-sheet-btn-cta"
              :disabled="submitting"
              :aria-label="$t('confirm_order')"
              @click="onSheetConfirm"
            >
              <span v-if="submitting" class="qr-spinner" aria-hidden="true" />
              <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" style="flex-shrink:0">
                <path d="M7 3v8a3 3 0 003 3v7M7 3v6M10 3v6M17 3c-1.5 0-3 1.5-3 4s1.5 4 3 4v10"
                  stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              {{ submitting ? ($t("sending") || "Sending…") : $t("confirm_order") }}
            </button>
            <button
              class="qr-sheet-btn-cancel"
              :disabled="submitting"
              @click="confirmSheet = false"
            >{{ $t("cancel") }}</button>
          </div>
        </template>

        <!-- Submitted / success state -->
        <template v-else>
          <div class="text-center py-4">
            <div class="qr-sheet-success-icon mx-auto mb-4">
              <v-icon class="qr-included-icon" size="32">fal fa-check</v-icon>
            </div>
            <div class="qr-sheet-title">{{ $t("order_sent_title") }}</div>
            <p class="qr-sheet-desc mt-2 mb-0 px-4">
              {{ $t("order_sent_desc") || "Your items will be brought to your table shortly. Keep ordering whenever you're ready." }}
            </p>
            <button
              class="qr-btn-cta mt-5"
              style="width: 100%"
              @click="onSheetDone"
            >{{ $t("back_to_menu") }}</button>
          </div>
        </template>
      </v-sheet>
    </v-bottom-sheet>

    <!-- Clear-all confirmation bottom sheet -->
    <v-bottom-sheet
      v-model="clearSheet"
      scrollable
      content-class="rounded-t-xl qr-confirm-sheet"
    >
      <v-sheet class="rounded-t-xl pa-4 pb-8 qr-confirm-sheet-inner">
        <div class="qr-sheet-grabber" aria-hidden="true" />
        <div class="qr-sheet-eyebrow text-center">{{ $t("confirm") || "Confirm" }}</div>
        <div class="qr-sheet-title text-center mt-1">{{ $t("remove_all") || "Remove all" }}?</div>
        <p class="qr-sheet-desc text-center mt-2 mb-0">
          {{ $t("remove_all_confirm_desc") || "All items will be removed from your cart. This cannot be undone." }}
        </p>
        <div class="qr-sheet-actions mt-5">
          <button class="qr-sheet-btn-danger" @click="confirmClearAll">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" style="flex-shrink:0">
              <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            {{ $t("remove_all") || "Remove all" }}
          </button>
          <button class="qr-sheet-btn-cancel" @click="clearSheet = false">{{ $t("cancel") }}</button>
        </div>
      </v-sheet>
    </v-bottom-sheet>

    <ScanBottomNav />
  </v-app>
</template>

<script>
import { myMixin } from "@/mixin/main";
import { toast } from "@/controllers/alert";
import { mainStore } from "@/stores/modules/cart";
import { mapGetters, mapMutations } from "vuex";
import ScanProductImage from "@/components/scan/ScanProductImage.vue";
import ScanBottomNav from "@/components/scan/ScanBottomNav.vue";
import ScanQrBarLogo from "@/components/scan/ScanQrBarLogo.vue";
import QrLanguageMenu from "@/components/qr_order/QrLanguageMenu.vue";

export default {
  mixins: [myMixin],
  components: { ScanProductImage, ScanBottomNav, ScanQrBarLogo, QrLanguageMenu },
  data() {
    return {
      defineStore: mainStore(),
      tables: [],
      confirmSheet: false,
      clearSheet: false,
      submitting: false,
      submitted: false,
      scrolled: false,
    };
  },
  mounted() {
    this._onScroll = () => { this.scrolled = window.scrollY > 8; };
    window.addEventListener("scroll", this._onScroll, { passive: true });
    try {
      const tableId = localStorage.getItem("table_id");
      if (String(tableId || "").toLowerCase() === "store") {
        const s = localStorage.getItem("store_id");
        const b = localStorage.getItem("branch_id");
        if (s && b) {
          this.$router.replace(`/scan/${s}/${b}/store`);
        }
      }
    } catch (e) {
      // ignore
    }
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this._onScroll);
  },
  computed: {
    ...mapGetters({
      overlay: "index/overlay",
      locales: "auth/locales",
    }),
    tableLabel() {
      try {
        return localStorage.getItem("table_id") || "";
      } catch {
        return "";
      }
    },
    isBuffetScan() {
      try {
        return localStorage.getItem("scan_store_type_code") === "BUFFET";
      } catch (e) {
        return false;
      }
    },
    buffetLines() {
      // Free items: buffet-included AND price_sale is 0 (no individual charge)
      return this.defineStore.cart.filter(
        (line) =>
          (line.isBuffetIncluded || line.item?.is_buffet_included === 1 || line.item?.is_buffet_included === true) &&
          Number(line.item?.price_sale || 0) === 0
      );
    },
    extraLines() {
      if (!this.isBuffetScan) return this.defineStore.cart;
      // Charged items: anything with price_sale > 0 (drinks etc.) OR not buffet-included at all
      return this.defineStore.cart.filter(
        (line) =>
          !(
            (line.isBuffetIncluded || line.item?.is_buffet_included === 1 || line.item?.is_buffet_included === true) &&
            Number(line.item?.price_sale || 0) === 0
          )
      );
    },
    buffetPlanPortion() {
      return this.defineStore.buffetPlanSubtotalSum();
    },
    extrasSubtotal() {
      return this.extraLines.reduce((sum, line) => sum + Number(line.price || 0), 0);
    },
    grandScanTotal() {
      return this.buffetPlanPortion + this.extrasSubtotal;
    },
    simpleCartTotal() {
      return this.defineStore.cart.reduce((a, b) => a + Number(b.price || 0), 0);
    },
    cartCurrencySymbol() {
      const cart = this.defineStore.cart || [];
      if (!cart.length) return "₭";
      const first = cart[0];
      const firstSymbol =
        first?.currency?.symbol || first?.item?.currency?.symbol || "";
      if (!firstSymbol) return "₭";

      const isMixed = cart.some((line) => {
        const symbol = line?.currency?.symbol || line?.item?.currency?.symbol || "";
        return symbol && symbol !== firstSymbol;
      });

      return isMixed ? "" : firstSymbol;
    },
    buffetPlanDetailCaption() {
      const rows = this.defineStore.buffet_plan_lines || [];
      const sym = this.cartCurrencySymbol || "₭";
      const fmt = (n) =>
        this.$options.filters.NumberFormat(Number(n || 0));
      if (!rows.length) {
        return "";
      }
      return rows
        .map((r) => `${r.persons} × ${sym}${fmt(r.unit_price)}`)
        .join(" · ");
    },
    buffetHeroPlans() {
      return this.defineStore.buffet_plan_lines || [];
    },
    buffetHeroGuests() {
      return this.buffetHeroPlans.reduce(
        (s, r) => s + Number(r.persons || 0),
        0
      );
    },
  },
  methods: {
    ...mapMutations({
      SET_OVERLAY: "index/SET_OVERLAY",
    }),
    unitLinePrice(line) {
      const q = Math.max(1, Number(line.qty) || 1);
      return Number(line.price || 0) / q;
    },
    getCurrencySymbol(item) {
      return item && item.currency && item.currency.symbol ? item.currency.symbol : "₭";
    },
    openConfirmSheet() {
      this.confirmSheet = true;
    },
    addToCartLine(line) {
      this.defineStore.addToCart({ that: this, payload: line.item });
    },
    async onSheetConfirm() {
      this.submitting = true;
      try {
        const store_id = localStorage.getItem("store_id");
        const branch_id = localStorage.getItem("branch_id");
        const table_id = localStorage.getItem("table_id");
        const result = await this.checkLocation(store_id, branch_id, table_id);
        if (result?.status) {
          const ok = await this.checkOut(store_id, branch_id, table_id);
          if (ok) {
            this.submitted = true;
          }
        } else {
          await toast(this, "checking_location_failed", "error");
          this.confirmSheet = false;
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.submitting = false;
      }
    },
    onSheetDone() {
      this.confirmSheet = false;
      this.submitting = false;
      this.submitted = false;
      this.$router.go(-1);
    },
    async checkOut(store_id, branch_id, table_id) {
      try {
        this.SET_OVERLAY(true);
        const { arr } = await Help.tableOrder(this.defineStore.cart);
        const body = {
          store_id: parseInt(store_id),
          branch_id: parseInt(branch_id),
          table_id: parseInt(table_id),
          status_order: "merge",
          is_online_order: 1,
          items: arr,
        };
        const { data } = await this.$http.post(`/qr/order`, body);
        if (data.status) {
          this.defineStore.deleteCart();
          await toast(this, data.message);
          return true;
        }
        return false;
      } catch (error) {
        console.log(error);
        return false;
      } finally {
        this.SET_OVERLAY(false);
      }
    },

    clearAllItems() {
      this.clearSheet = true;
    },
    confirmClearAll() {
      this.defineStore.deleteCart();
      this.clearSheet = false;
    },
    async removeCart(item) {
      const body = {
        that: this,
        payload: item,
      };
      this.defineStore.removeCart(body);
    },
    decrementCart(item) {
      this.defineStore.decrementCart({ payload: item });
    },
    changeLocale(code) {
      localStorage.setItem("locale", code);
      this.$i18n.locale = code;
    },
  },
};
</script>

<style scoped>
.scan-cart-page {
  background-color: var(--qr-bg);
  touch-action: manipulation;
  padding-bottom: 0;
}

.qr-bar {
  background: var(--qr-appbar-tint-clear) !important;
  transition: background 0.2s, border-color 0.2s;
  border-bottom: 0.5px solid transparent !important;
}

.qr-bar--scrolled {
  background: var(--qr-appbar-tint-scrolled) !important;
  -webkit-backdrop-filter: blur(18px) saturate(170%);
  backdrop-filter: blur(18px) saturate(170%);
  border-bottom-color: rgba(40,28,16,0.07) !important;
}

.qr-bar ::v-deep .v-toolbar__content {
  background: transparent !important;
}

.qr-clear-btn {
  font-size: 12px;
  font-weight: 600;
  color: var(--qr-muted);
  background: rgba(40, 28, 16, 0.06);
  border: 0;
  border-radius: 999px;
  padding: 6px 12px;
  cursor: pointer;
  font-family: inherit;
  touch-action: manipulation;
  transition: background 0.15s, color 0.15s;
}

.qr-clear-btn:active {
  background: rgba(40, 28, 16, 0.12);
  color: var(--qr-text);
}

.qr-icon-btn {
  background: rgba(40,28,16,0.05) !important;
  border-radius: 999px !important;
  min-width: 38px !important;
  min-height: 38px !important;
}

.scan-touch-target {
  min-width: 44px !important;
  min-height: 44px !important;
}

.qr-bar__title-area {
  min-width: 0;
}

.qr-bar__subtitle {
  font-size: 11px;
  color: var(--qr-muted);
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  line-height: 1.1;
}

.qr-bar__title {
  font-size: 17px;
  font-weight: 700;
  color: var(--qr-text);
  letter-spacing: -0.3px;
  line-height: 1.2;
}

.qr-cart-content {
  padding-top: 16px;
  padding-bottom: calc(160px + env(safe-area-inset-bottom, 0px));
}

/* Empty state */
.qr-cart-empty {
  min-height: calc(100vh - 65px - 64px - env(safe-area-inset-bottom, 0px));
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 24px;
  text-align: center;
}

.qr-cart-empty__inner {
  width: 100%;
  max-width: 420px;
}

.qr-text { color: var(--qr-text); }
.qr-muted-text { color: var(--qr-muted); }
.qr-accent-text { color: var(--qr-accent) !important; }
.qr-included-text { color: var(--qr-included-fg) !important; }
.qr-mono { font-variant-numeric: tabular-nums; }

/* Buffet plan card */
.qr-cart-plan-card {
  background: var(--qr-card-bg);
  border: 0.5px solid var(--qr-card-border);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(40,28,16,0.06);
}

.qr-cart-plan-card__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px 10px;
  border-bottom: 0.5px solid var(--qr-card-border);
}

.qr-cart-plan-card__icon {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: var(--qr-accent-soft);
  color: var(--qr-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.qr-cart-plan-card__info { flex: 1; min-width: 0; }

.qr-cart-plan-card__name {
  font-size: 14px;
  font-weight: 700;
  color: var(--qr-text);
  letter-spacing: -0.2px;
}

.qr-cart-plan-card__sub {
  font-size: 11px;
  color: var(--qr-muted);
  margin-top: 2px;
}

.qr-cart-plan-card__badge {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 999px;
  background: var(--qr-included-bg);
  color: var(--qr-included-fg);
  flex-shrink: 0;
}

.qr-cart-plan-card__body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 14px;
  gap: 10px;
}

.qr-cart-plan-card__plan-name {
  font-size: 14.5px;
  font-weight: 600;
  color: var(--qr-text);
  letter-spacing: -0.1px;
}

.qr-cart-plan-card__plan-detail {
  font-size: 11.5px;
  color: var(--qr-muted);
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
}

.qr-cart-plan-card__total {
  font-size: 16px;
  font-weight: 700;
  color: var(--qr-accent);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.3px;
  flex-shrink: 0;
}

/* Section headers */
.qr-cart-section { margin-bottom: 20px; }

.qr-cart-section__header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 10px;
  padding: 0 4px;
}

.qr-cart-section__title {
  font-size: 14.5px;
  font-weight: 700;
  color: var(--qr-text);
  letter-spacing: -0.3px;
}

.qr-cart-section__sub {
  font-size: 11px;
  color: var(--qr-muted);
  margin-left: 8px;
}

/* Cart line cards */
.qr-cart-lines {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.qr-cart-line-card {
  background: var(--qr-card-bg);
  border: 0.5px solid var(--qr-card-border);
  border-radius: 14px;
  padding: 10px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 3px 10px rgba(40,28,16,0.04);
}

.qr-cart-line-card__thumb {
  width: 56px;
  height: 56px;
  border-radius: 11px;
  overflow: hidden;
  flex-shrink: 0;
}

.qr-cart-line-card__info {
  flex: 1;
  min-width: 0;
}

.qr-cart-line-card__name {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--qr-text);
  letter-spacing: -0.1px;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qr-cart-line-card__sub {
  font-size: 11px;
  color: var(--qr-muted);
  margin-top: 3px;
  font-variant-numeric: tabular-nums;
}

.qr-cart-line-card__total {
  min-width: 58px;
  text-align: right;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.2px;
  flex-shrink: 0;
}

/* Inline qty stepper */
.qr-qty-stepper {
  display: flex;
  align-items: center;
  gap: 2px;
  background: rgba(40,28,16,0.05);
  border-radius: 999px;
  padding: 3px;
  flex-shrink: 0;
}

.qr-qty-stepper__btn {
  width: 28px;
  height: 28px;
  min-width: 44px;
  min-height: 44px;
  border-radius: 999px;
  border: 0;
  background: transparent;
  color: var(--qr-text);
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-qty-stepper__btn--plus {
  background: var(--qr-accent);
  color: #fff;
  font-weight: 700;
}

.qr-qty-stepper__count {
  min-width: 16px;
  text-align: center;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--qr-text);
  font-variant-numeric: tabular-nums;
}

/* Totals breakdown */
.qr-cart-totals-card {
  background: var(--qr-subtle-bg);
  border: 0.5px solid var(--qr-card-border);
  border-radius: 16px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.qr-totals-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  font-size: 12.5px;
  color: var(--qr-muted);
}

.qr-totals-row span:last-child {
  font-size: 13px;
  font-weight: 600;
  color: var(--qr-text);
  font-variant-numeric: tabular-nums;
}

/* Notes button */
.qr-notes-btn {
  width: 100%;
  background: transparent;
  border: 1px dashed rgba(40,28,16,0.16);
  border-radius: 14px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--qr-muted);
  font-size: 12.5px;
  font-weight: 500;
  cursor: default;
  text-align: left;
  font-family: inherit;
}

.qr-notes-btn span { flex: 1; }

/* Sticky bottom bar */
.qr-cart-sticky-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: calc(64px + env(safe-area-inset-bottom, 0px));
  z-index: 10;
  padding: 12px 16px 16px;
  background: var(--qr-appbar-tint-sticky);
  -webkit-backdrop-filter: blur(18px) saturate(170%);
  backdrop-filter: blur(18px) saturate(170%);
  border-top: 0.5px solid rgba(40,28,16,0.08);
  display: flex;
  align-items: center;
  gap: 12px;
}

.qr-cart-sticky-bar__total { flex: 1; min-width: 0; }

.qr-cart-sticky-bar__label {
  font-size: 10.5px;
  color: var(--qr-muted);
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
}

.qr-cart-sticky-bar__price {
  font-size: 22px;
  font-weight: 800;
  color: var(--qr-text);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.6px;
  line-height: 1.1;
  margin-top: 1px;
}

.qr-cart-sticky-bar__cta {
  min-width: 160px;
}

/* CTA button (shared) */
.qr-btn-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 54px;
  padding: 0 24px;
  border-radius: 999px;
  border: 0;
  background: var(--qr-cta-gradient);
  color: #fff;
  font-size: 14.5px;
  font-weight: 700;
  letter-spacing: -0.2px;
  cursor: pointer;
  font-family: inherit;
  box-shadow: var(--qr-cta-shadow);
  touch-action: manipulation;
  flex-shrink: 0;
  transition: box-shadow 0.15s, transform 0.1s;
}

.qr-btn-cta:active {
  transform: scale(0.97);
  box-shadow: var(--qr-cta-shadow-active);
}

.qr-btn-cta:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}

/* Bottom sheet styles */
.qr-confirm-sheet-inner {
  touch-action: manipulation;
  background: #ffffff;
}

.qr-sheet-grabber {
  width: 36px;
  height: 4px;
  border-radius: 999px;
  background: rgba(40,28,16,0.18);
  margin: 0 auto 16px;
}

.qr-sheet-eyebrow {
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--qr-muted);
}

.qr-sheet-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--qr-text);
  letter-spacing: -0.5px;
  line-height: 1.2;
}

.qr-sheet-desc {
  font-size: 12.5px;
  color: var(--qr-muted);
  line-height: 1.45;
}

.qr-sheet-summary-card {
  background: rgba(40,28,16,0.04);
  border: 0.5px solid rgba(40,28,16,0.08);
  border-radius: 14px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.qr-sheet-summary-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.qr-sheet-summary-row__icon {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  flex-shrink: 0;
  background: rgba(40,28,16,0.05);
  color: var(--qr-text);
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-sheet-summary-row__label {
  flex: 1;
  font-size: 12.5px;
  color: var(--qr-muted);
  font-weight: 500;
}

.qr-sheet-summary-row__value {
  font-size: 13px;
  font-weight: 700;
  color: var(--qr-text);
}

.qr-sheet-grand-total {
  padding: 12px 14px;
  border-radius: 14px;
  background: var(--qr-soft-highlight-bg);
  border: 0.5px solid var(--qr-soft-highlight-border);
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.qr-sheet-grand-total__label {
  font-size: 12.5px;
  color: var(--qr-muted);
  font-weight: 600;
}

.qr-sheet-grand-total__value {
  font-size: 20px;
  font-weight: 800;
  color: var(--qr-accent);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.4px;
}

.qr-sheet-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.qr-sheet-btn-cta {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 58px;
  border-radius: 18px;
  border: 0;
  background: var(--qr-cta-gradient);
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  letter-spacing: -0.3px;
  box-shadow: var(--qr-sheet-cta-shadow);
  touch-action: manipulation;
  transition: box-shadow 0.15s, transform 0.1s;
}

.qr-sheet-btn-cta:active {
  transform: scale(0.98);
  box-shadow: var(--qr-sheet-cta-shadow-active);
}

.qr-sheet-btn-cta:disabled {
  opacity: 0.85;
  cursor: wait;
}

.qr-sheet-btn-danger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 58px;
  border-radius: 18px;
  border: 0;
  background: linear-gradient(135deg, #e05252 0%, #c0392b 100%);
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  letter-spacing: -0.3px;
  box-shadow: 0 14px 32px rgba(192, 57, 43, 0.32), inset 0 1px 0 rgba(255, 255, 255, 0.2);
  touch-action: manipulation;
  transition: box-shadow 0.15s, transform 0.1s;
}

.qr-sheet-btn-danger:active {
  transform: scale(0.98);
  box-shadow: 0 6px 16px rgba(192, 57, 43, 0.22);
}

.qr-sheet-btn-cancel {
  width: 100%;
  height: 42px;
  border: 0;
  background: transparent;
  color: var(--qr-muted);
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  touch-action: manipulation;
}

.qr-sheet-btn-cancel:disabled {
  opacity: 0.4;
  cursor: wait;
}

/* Success icon */
.qr-sheet-success-icon {
  width: 64px;
  height: 64px;
  border-radius: 999px;
  background: rgba(79,122,68,0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Spinner */
.qr-spinner {
  width: 16px;
  height: 16px;
  border-radius: 999px;
  border: 2px solid rgba(255,255,255,0.35);
  border-top-color: #fff;
  display: inline-block;
  animation: qr-spin 0.7s linear infinite;
}

@keyframes qr-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .qr-spinner { animation: none; }
  .qr-btn-cta,
  .qr-sheet-btn-cta,
  .qr-cart-line-card { transition: none !important; }
}
</style>
